import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
Copyright 2017 - 2021 The Matrix.org Foundation C.I.C.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/

import { safeSet } from "../../utils.js";
import { MigrationState, SESSION_BATCH_SIZE } from "./base.js";
function encodeSessionKey(senderCurve25519Key, sessionId) {
  return encodeURIComponent(senderCurve25519Key) + "/" + encodeURIComponent(sessionId);
}
function decodeSessionKey(key) {
  const keyParts = key.split("/");
  const senderKey = decodeURIComponent(keyParts[0]);
  const sessionId = decodeURIComponent(keyParts[1]);
  return {
    senderKey,
    sessionId
  };
}

/**
 * Internal module. in-memory storage for e2e.
 */

export class MemoryCryptoStore {
  constructor() {
    _defineProperty(this, "migrationState", MigrationState.NOT_STARTED);
    _defineProperty(this, "account", null);
    _defineProperty(this, "crossSigningKeys", null);
    _defineProperty(this, "privateKeys", {});
    _defineProperty(this, "sessions", {});
    _defineProperty(this, "inboundGroupSessions", {});
    _defineProperty(this, "inboundGroupSessionsWithheld", {});
    // Opaque device data object
    _defineProperty(this, "rooms", {});
    _defineProperty(this, "sessionsNeedingBackup", {});
  }
  /**
   * Returns true if this CryptoStore has ever been initialised (ie, it might contain data).
   *
   * Implementation of {@link CryptoStore.containsData}.
   *
   * @internal
   */
  async containsData() {
    // If it contains anything, it should contain an account.
    return this.account !== null;
  }

  /**
   * Ensure the database exists and is up-to-date.
   *
   * This must be called before the store can be used.
   *
   * @returns resolves to the store.
   */
  async startup() {
    // No startup work to do for the memory store.
    return this;
  }

  /**
   * Delete all data from this store.
   *
   * @returns Promise which resolves when the store has been cleared.
   */
  deleteAllData() {
    return Promise.resolve();
  }

  /**
   * Get data on how much of the libolm to Rust Crypto migration has been done.
   *
   * Implementation of {@link CryptoStore.getMigrationState}.
   *
   * @internal
   */
  async getMigrationState() {
    return this.migrationState;
  }

  /**
   * Set data on how much of the libolm to Rust Crypto migration has been done.
   *
   * Implementation of {@link CryptoStore.setMigrationState}.
   *
   * @internal
   */
  async setMigrationState(migrationState) {
    this.migrationState = migrationState;
  }

  // Olm Account

  getAccount(txn, func) {
    func(this.account);
  }
  storeAccount(txn, accountPickle) {
    this.account = accountPickle;
  }
  getCrossSigningKeys(txn, func) {
    func(this.crossSigningKeys);
  }
  getSecretStorePrivateKey(txn, func, type) {
    const result = this.privateKeys[type];
    func(result || null);
  }
  storeSecretStorePrivateKey(txn, type, key) {
    this.privateKeys[type] = key;
  }

  // Olm Sessions

  countEndToEndSessions(txn, func) {
    let count = 0;
    for (const deviceSessions of Object.values(this.sessions)) {
      count += Object.keys(deviceSessions).length;
    }
    func(count);
  }
  getEndToEndSession(deviceKey, sessionId, txn, func) {
    const deviceSessions = this.sessions[deviceKey] || {};
    func(deviceSessions[sessionId] || null);
  }
  getEndToEndSessions(deviceKey, txn, func) {
    func(this.sessions[deviceKey] || {});
  }
  storeEndToEndSession(deviceKey, sessionId, sessionInfo, txn) {
    let deviceSessions = this.sessions[deviceKey];
    if (deviceSessions === undefined) {
      deviceSessions = {};
      this.sessions[deviceKey] = deviceSessions;
    }
    safeSet(deviceSessions, sessionId, sessionInfo);
  }

  /**
   * Fetch a batch of Olm sessions from the database.
   *
   * Implementation of {@link CryptoStore.getEndToEndSessionsBatch}.
   *
   * @internal
   */
  async getEndToEndSessionsBatch() {
    const result = [];
    for (const deviceSessions of Object.values(this.sessions)) {
      for (const session of Object.values(deviceSessions)) {
        result.push(session);
        if (result.length >= SESSION_BATCH_SIZE) {
          return result;
        }
      }
    }
    if (result.length === 0) {
      // No sessions left.
      return null;
    }

    // There are fewer sessions than the batch size; return the final batch of sessions.
    return result;
  }

  /**
   * Delete a batch of Olm sessions from the database.
   *
   * Implementation of {@link CryptoStore.deleteEndToEndSessionsBatch}.
   *
   * @internal
   */
  async deleteEndToEndSessionsBatch(sessions) {
    for (const {
      deviceKey,
      sessionId
    } of sessions) {
      const deviceSessions = this.sessions[deviceKey] || {};
      delete deviceSessions[sessionId];
      if (Object.keys(deviceSessions).length === 0) {
        // No more sessions for this device.
        delete this.sessions[deviceKey];
      }
    }
  }

  // Inbound Group Sessions

  getEndToEndInboundGroupSession(senderCurve25519Key, sessionId, txn, func) {
    const k = encodeSessionKey(senderCurve25519Key, sessionId);
    func(this.inboundGroupSessions[k] || null, this.inboundGroupSessionsWithheld[k] || null);
  }
  storeEndToEndInboundGroupSession(senderCurve25519Key, sessionId, sessionData, txn) {
    const k = encodeSessionKey(senderCurve25519Key, sessionId);
    this.inboundGroupSessions[k] = sessionData;
  }

  /**
   * Count the number of Megolm sessions in the database.
   *
   * Implementation of {@link CryptoStore.countEndToEndInboundGroupSessions}.
   *
   * @internal
   */
  async countEndToEndInboundGroupSessions() {
    return Object.keys(this.inboundGroupSessions).length;
  }

  /**
   * Fetch a batch of Megolm sessions from the database.
   *
   * Implementation of {@link CryptoStore.getEndToEndInboundGroupSessionsBatch}.
   *
   * @internal
   */
  async getEndToEndInboundGroupSessionsBatch() {
    const result = [];
    for (const [key, session] of Object.entries(this.inboundGroupSessions)) {
      result.push(_objectSpread(_objectSpread({}, decodeSessionKey(key)), {}, {
        sessionData: session,
        needsBackup: key in this.sessionsNeedingBackup
      }));
      if (result.length >= SESSION_BATCH_SIZE) {
        return result;
      }
    }
    if (result.length === 0) {
      // No sessions left.
      return null;
    }

    // There are fewer sessions than the batch size; return the final batch of sessions.
    return result;
  }

  /**
   * Delete a batch of Megolm sessions from the database.
   *
   * Implementation of {@link CryptoStore.deleteEndToEndInboundGroupSessionsBatch}.
   *
   * @internal
   */
  async deleteEndToEndInboundGroupSessionsBatch(sessions) {
    for (const {
      senderKey,
      sessionId
    } of sessions) {
      const k = encodeSessionKey(senderKey, sessionId);
      delete this.inboundGroupSessions[k];
    }
  }

  // E2E rooms

  getEndToEndRooms(txn, func) {
    func(this.rooms);
  }
  markSessionsNeedingBackup(sessions) {
    for (const session of sessions) {
      const sessionKey = encodeSessionKey(session.senderKey, session.sessionId);
      this.sessionsNeedingBackup[sessionKey] = true;
    }
    return Promise.resolve();
  }

  // Session key backups

  doTxn(mode, stores, func) {
    return Promise.resolve(func(null));
  }
}
//# sourceMappingURL=memory-crypto-store.js.map